import React from 'react';
import { Card } from '@/components/ui/card';
import { Building2, Clock, Lock, Users2, RefreshCw, AlertTriangle } from 'lucide-react';

const WD_NAMES = ['Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota', 'Nedeľa'];

function isClosed(oh) {
  return !oh || (oh.open === '00:00' && oh.close === '00:00');
}

function fmtSync(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('sk-SK', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return null; }
}

// Nezrovnalosti medzi otváracími hodinami + potrebou (center-shift-times) a druhým zdrojom (plan-shift-counts).
function computeWarnings(wp) {
  const out = [];
  const sc = wp.shift_counts;
  const oh = wp.opening_hours || {};
  const need = wp.needed_by_day || {};
  if (!sc || !sc.days) return out;
  const byNum = {};
  sc.days.forEach((d) => { byNum[d.day_number] = d; });
  for (let wd = 0; wd < 7; wd++) {
    const closed = isClosed(oh[String(wd)]);
    const d = byNum[wd + 1];
    const planPeak = d ? Math.max(0, ...(d.shifts || []).map((s) => s.total || 0)) : 0;
    const n = need[String(wd)] || {};
    const needPeak = Math.max(n.morning || 0, n.evening || 0);
    const name = WD_NAMES[wd];
    if (closed && planPeak > 0) {
      out.push(`${name}: v otváracích hodinách je ZATVORENÉ, ale druhý zdroj (plán) počíta ${planPeak} ľudí.`);
    } else if (!closed && d && planPeak === 0) {
      out.push(`${name}: je OTVORENÉ, ale druhý zdroj (plán) neuvádza žiadnych ľudí.`);
    } else if (!closed && d && needPeak !== planPeak) {
      out.push(`${name}: otváracie hodiny žiadajú ${needPeak} ľudí, ale plán (podľa pozícií) ${planPeak}.`);
    }
  }
  return out;
}

function ShiftCountsTable({ sc }) {
  if (!sc || !sc.days || sc.days.length === 0) {
    return (
      <p className="text-[12px] text-slate-400">
        Počty na zmenách z TiDo nie sú k dispozícii. Klikni <b>Synchronizovať</b>.
      </p>
    );
  }
  const rights = sc.rights || [];
  return (
    <table className="w-full text-sm border border-slate-200 rounded-md overflow-hidden" data-testid="shift-counts-table">
      <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
        <tr>
          <th className="text-left px-3 py-1.5 font-medium">Deň</th>
          <th className="text-left px-2 py-1.5 font-medium">Zmena</th>
          {rights.map((r) => <th key={r.shortcut} className="text-center px-2 py-1.5 font-medium w-16">{r.shortcut}</th>)}
          <th className="text-center px-2 py-1.5 font-medium w-16">Spolu</th>
        </tr>
      </thead>
      <tbody>
        {sc.days.map((d, di) => (
          (d.shifts || []).map((sh, si) => (
            <tr key={`${di}-${si}`} className={`border-t border-slate-100 ${si === 0 ? 'border-t-slate-200' : ''}`}>
              {si === 0 && (
                <td rowSpan={(d.shifts || []).length} className="px-3 py-1.5 font-medium text-slate-700 align-middle border-r border-slate-100">{d.name}</td>
              )}
              <td className="px-2 py-1.5 text-slate-500">{sh.name} <span className="font-mono text-slate-300">({sh.shortcut})</span></td>
              {rights.map((r) => {
                const rr = (sh.rights || []).find((x) => x.shortcut === r.shortcut);
                const c = rr ? rr.count : 0;
                return <td key={r.shortcut} className={`px-2 py-1.5 text-center ${c > 0 ? 'text-slate-700' : 'text-slate-300'}`}>{c}</td>;
              })}
              <td className={`px-2 py-1.5 text-center font-semibold ${sh.total > 0 ? 'text-slate-900' : 'text-slate-300'}`}>{sh.total}</td>
            </tr>
          ))
        ))}
      </tbody>
    </table>
  );
}

function StoreDetail({ wp }) {
  if (!wp) return <Card className="p-8 text-center text-slate-400">Vyberte stredisko.</Card>;
  const synced = fmtSync(wp.synced_at);
  const warnings = computeWarnings(wp);
  return (
    <Card className="p-4 space-y-5" data-testid="store-detail">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center"><Building2 className="w-4.5 h-4.5" /></div>
          <div className="font-heading font-bold text-slate-900" data-testid="store-name">{wp.name}</div>
        </div>
        {synced && (
          <div data-testid="store-synced-at" className="flex items-center gap-1.5 text-[11px] text-slate-400 whitespace-nowrap">
            <RefreshCw className="w-3 h-3" /> Synchronizované {synced}
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 text-[12px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
        <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
        <span>Nastavenia strediska sa spravujú v <b>TiDo</b>. Tu sú len na čítanie – po zmene klikni <b>Synchronizovať</b>.</span>
      </div>

      {warnings.length > 0 && (
        <div data-testid="store-warnings" className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5">
          <div className="flex items-center gap-1.5 font-semibold mb-1.5">
            <AlertTriangle className="w-4 h-4" /> Nezrovnalosti v dátach z TiDo ({warnings.length})
          </div>
          <ul className="list-disc pl-5 space-y-0.5">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
          <div className="text-[11px] text-amber-700/80 mt-1.5">Môže ísť o staré nastavenie. Uprav v TiDo a klikni Synchronizovať.</div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> Otváracie hodiny a počet ľudí</h3>
        <table className="w-full text-sm border border-slate-200 rounded-md overflow-hidden max-w-md" data-testid="opening-hours-table">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-1.5 font-medium">Deň</th>
              <th className="text-left px-2 py-1.5 font-medium">Hodiny</th>
              <th className="text-center px-2 py-1.5 font-medium w-16">Ráno</th>
              <th className="text-center px-2 py-1.5 font-medium w-16">Večer</th>
            </tr>
          </thead>
          <tbody>
            {WD_NAMES.map((name, wd) => {
              const oh = (wp.opening_hours || {})[String(wd)];
              const n = (wp.needed_by_day || {})[String(wd)] || {};
              const closed = isClosed(oh);
              return (
                <tr key={wd} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-medium text-slate-700">{name}</td>
                  <td className="px-2 py-1.5">{closed
                    ? <span className="text-slate-300">Zatvorené</span>
                    : <span className="font-mono text-slate-600">{oh.open}–{oh.close}</span>}</td>
                  <td className={`px-2 py-1.5 text-center font-semibold ${closed ? 'text-slate-300' : 'text-slate-900'}`}>{closed ? '—' : (n.morning ?? 0)}</td>
                  <td className={`px-2 py-1.5 text-center font-semibold ${closed ? 'text-slate-300' : 'text-slate-900'}`}>{closed ? '—' : (n.evening ?? 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-[11px] text-slate-400 mt-1.5">Celkový počet ľudí na zmene (zdroj: otváracie hodiny strediska v TiDo).</p>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Users2 className="w-4 h-4" /> Rozdelenie podľa pozícií (Vedúci / Pracovník)</h3>
        <ShiftCountsTable sc={wp.shift_counts} />
        <p className="text-[11px] text-slate-400 mt-1.5">Druhý zdroj z TiDo – rozlišuje, koľko na zmene má byť vedúcich/zástupcov a koľko pracovníkov.</p>
      </div>

      {wp.shifts?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Zmeny</h3>
          <div className="flex flex-wrap gap-2">
            {wp.shifts.map((s) => (
              <span key={s.code} className="text-xs border border-slate-200 rounded px-2 py-1 bg-slate-50 font-mono">
                {s.code}: {s.start}–{s.end} ({s.hours}h)
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function WorkplacesPage({ workplaces, selectedId, onSelect, embedded }) {
  const selected = workplaces.find((w) => w.id === selectedId);

  if (embedded) {
    return <div className="max-w-2xl mx-auto" data-testid="workplaces-page"><StoreDetail wp={selected} /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="workplaces-page">
      <div className="lg:col-span-1 space-y-1.5">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-1"><Building2 className="w-4 h-4" /> Strediská</h2>
        {workplaces.map((w) => (
          <button
            key={w.id}
            data-testid={`workplace-${w.id}`}
            onClick={() => onSelect(w.id)}
            className={`w-full text-left px-3 py-2 rounded-md border text-sm transition-colors ${selectedId === w.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50 border-slate-200'}`}
          >
            {w.name}
          </button>
        ))}
        {workplaces.length === 0 && <div className="text-xs text-slate-400 px-1">Žiadne strediská.</div>}
      </div>
      <div className="lg:col-span-2">
        <StoreDetail wp={selected} />
      </div>
    </div>
  );
}
